import { Box, StyledEngineProvider, Tooltip } from "@mui/material";
import { ThemeWrapper } from "@verity/components";
import { CoreCheckbox } from "@verity/components/core";
import { produce } from "immer";
import React, { useEffect } from "react";
import { z } from "zod";
import i18n from "../../../../i18n";
import InfoSvg from "../../../../images/info.svg?react";
import styles from "./index.module.css";

export const allowQuizAttemptOnBrowserSettingSchema = z.object({
	allowQuizAttemptOnChrome: z.boolean(),
	allowQuizAttemptOnFirefox: z.boolean(),
	allowQuizAttemptOnSafari: z.boolean(),
});

export type AllowQuizAttemptOnBrowserSettingData = z.infer<typeof allowQuizAttemptOnBrowserSettingSchema>;

export const AllowQuizAttemptOnBrowserSetting = ({
	loadData,
	onSettingChange,
}: {
	loadData: AllowQuizAttemptOnBrowserSettingData;

	onSettingChange: (value?: AllowQuizAttemptOnBrowserSettingData) => void;
	disabled?: boolean;
}) => {
	const [
		{ allowQuizAttemptOnChrome, allowQuizAttemptOnFirefox, allowQuizAttemptOnSafari },
		setAllowQuizAttemptOnBrowser,
	] = React.useState(loadData);

	const browserOptions: {
		checked: boolean;
		disabled: boolean;
		onToggle: (checked: boolean) => void;
		label: string;
		description: string;
	}[] = [
		{
			checked: true,
			disabled: true,
			onToggle: () => {}, // no-op
			label: i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.browsers.chrome.label"),
			description: i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.browsers.chrome.description"),
		},
		{
			checked: allowQuizAttemptOnFirefox,
			disabled: false,
			onToggle: (checked: boolean) => {
				setAllowQuizAttemptOnBrowser(
					produce((draft) => {
						draft.allowQuizAttemptOnFirefox = checked;
					}),
				);
			},
			label: i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.browsers.firefox.label"),
			description: i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.browsers.firefox.description"),
		},
		{
			checked: allowQuizAttemptOnSafari,
			disabled: false,
			onToggle: (checked: boolean) => {
				setAllowQuizAttemptOnBrowser(
					produce((draft) => {
						draft.allowQuizAttemptOnSafari = checked;
					}),
				);
			},
			label: i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.browsers.safari.label"),
			description: i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.browsers.safari.description"),
		},
	];

	useEffect(() => {
		onSettingChange({ allowQuizAttemptOnChrome, allowQuizAttemptOnFirefox, allowQuizAttemptOnSafari });
	}, [onSettingChange, allowQuizAttemptOnChrome, allowQuizAttemptOnFirefox, allowQuizAttemptOnSafari]);

	return (
		<StyledEngineProvider injectFirst>
			<ThemeWrapper>
				<Box className={styles.rootContainer}>
					<span className={styles.settingDescription}>
						{i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.description")}
					</span>
					<Box className={styles.browserOptionsContainer}>
						<span className={styles.browserOptionsTitle}>
							{i18n.t("portal.components.settings.allowQuizAttemptOnBrowser.browsers.title")}:
						</span>
						{browserOptions.map((browserOption) => (
							<Box key={browserOption.label} className={styles.browserOptionEntry}>
								<CoreCheckbox
									checked={browserOption.checked}
									onChange={(e) => browserOption.onToggle(e.target.checked)}
									disabled={browserOption.disabled}
								/>
								<Box className={styles.browserOptionEntryLabel}>{browserOption.label}</Box>
								<Tooltip title={browserOption.description} placement="top">
									<Box sx={{ display: "flex", alignItems: "center" }}>
										<InfoSvg className={styles.browserOptionEntryTooltipIcon} />
									</Box>
								</Tooltip>
							</Box>
						))}
					</Box>
				</Box>
			</ThemeWrapper>
		</StyledEngineProvider>
	);
};
